import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AuthError,
  signUp as amplifySignUp,
  confirmSignUp,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  resendSignUpCode,
  signInWithRedirect,
  getCurrentUser as amplifyGetCurrentUser,
} from "aws-amplify/auth";
import { useCallback, useEffect, useState } from "react";
import * as React from "react";

import { orgStorage } from "@/shared/auth";
import { FeatureFlagService } from "@/shared/domain/featureFlags";
import { featureFlagQueryKeys } from "@/shared/domain/featureFlags/hooks/featureFlagQueryKeys";
import { refetchFeatureFlagStates } from "@/shared/domain/featureFlags/hooks/refetchFeatureFlagStates";
import { OrgRecord, type OrgUserRecord } from "@/shared/domain/orgs";
import { UserRecord } from "@/shared/domain/users";
import { Env } from "@/shared/environment";
import { isAbortError } from "@/shared/errors";
import { RobotoDomainException } from "@/shared/http";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { UserIdToken } from "@/types";

import { useHttpClient } from "../HttpClient";

import { AuthContext, type SignInResult } from "./AuthContext";

interface AuthProviderProps {
  children: React.ReactNode;
}

interface AuthProviderState {
  availableOrganizations: OrgRecord[] | null;
  currentOrganization: OrgRecord | null;
  currentUser: UserRecord | null;
  initialAuthCheckComplete: boolean;
  isAuthenticated: boolean;
  pendingAuthOperationCount: number;
}

/** Keyed by user so that a session never reads roles cached for the previous one. */
const myOrgRolesQueryKeys = {
  all: () => ["myOrgRoles"] as const,
  forUser: (userId: string) => ["myOrgRoles", userId] as const,
};

/**
 * How long a read of the caller's roles counts as current. Roles can change in
 * another tab or by another admin, so a window focus rereads them once this has
 * passed.
 */
const MY_ORG_ROLES_STALE_TIME_MS = 30_000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { httpClient, setResourceOwnerId } = useHttpClient();
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthProviderState>({
    availableOrganizations: [],
    currentUser: null,
    currentOrganization: null,
    initialAuthCheckComplete: false,
    isAuthenticated: false,
    // A race-condition safe count for pending auth operations
    // Increment before starting an operation that should cause the UI to show a loading indicator, decrement after it completes.
    pendingAuthOperationCount: 0,
  });

  React.useEffect(
    // Note(Pratik) We may want to opt for a PubSub pattern here so
    // that AuthProvider doesn't take a dependency on ErrorMonitoringService and
    // other dependencies that live outside react.
    function onUserChange() {
      if (authState.currentUser === null) {
        return;
      }

      ErrorMonitoringService.setUser(authState.currentUser);
    },
    [authState.currentUser],
  );

  React.useEffect(
    function onOrgChange() {
      setResourceOwnerId(authState.currentOrganization?.org_id);

      if (authState.currentOrganization === null) {
        return;
      }

      ErrorMonitoringService.setOrg(authState.currentOrganization);
    },
    [authState.currentOrganization, setResourceOwnerId],
  );

  const getCurrentOrganization = useCallback((): OrgRecord | null => {
    if (authState.currentOrganization) {
      return authState.currentOrganization;
    }

    return orgStorage.getCurrentOrg();
  }, [authState.currentOrganization]);

  const deleteCurrentOrganizationFromLocalStorage = useCallback(() => {
    orgStorage.clearCurrentOrg();
  }, []);

  const deleteCurrentOrganization = useCallback(() => {
    deleteCurrentOrganizationFromLocalStorage();

    setAuthState((prevState) => ({
      ...prevState,
      currentOrganization: null,
    }));
  }, [deleteCurrentOrganizationFromLocalStorage]);

  /**
   * Prefetches feature flags and caches them for the session
   */
  const seedFeatureFlags = useCallback(() => {
    // Deliberately not awaited: it races the user/org requests instead of
    // blocking the critical path.
    void queryClient.prefetchQuery({
      queryKey: featureFlagQueryKeys.states(),
      queryFn: () =>
        new FeatureFlagService(httpClient).checkAllFeatureFlags({
          orgId: orgStorage.getCurrentOrg()?.org_id,
        }),
      staleTime: Infinity,
    });
  }, [httpClient, queryClient]);

  /**
   * Drop the cached flag matrix. Required on both sign-in and sign-out: the
   * cache key carries no user identity, so a second session in the same tab
   * would otherwise read the previous user's flags.
   */
  const clearFeatureFlags = useCallback(() => {
    queryClient.removeQueries({ queryKey: featureFlagQueryKeys.states() });
  }, [queryClient]);

  const setCurrentOrganization = (org: OrgRecord) => {
    orgStorage.setCurrentOrg(org);

    // The cached matrix has no entry for an org joined mid-session, or for an
    // org a Roboto admin views without belonging to it. The refetch reads the
    // new org from storage, so it must follow the write above.
    void refetchFeatureFlagStates(queryClient);

    setAuthState((prevState) => ({
      ...prevState,
      currentOrganization: org,
    }));
    void refreshMyOrgRoles();
  };

  const retrieveMyOrgRoles = useCallback(
    async (signal?: AbortSignal): Promise<OrgUserRecord[]> => {
      const response = await httpClient.get("v1/users/roles", { signal });
      return response.json<OrgUserRecord[]>();
    },
    [httpClient],
  );

  const retrieveOrgList = useCallback(
    async (signal?: AbortSignal): Promise<OrgRecord[]> => {
      const response = await httpClient.get("v1/users/orgs", { signal });
      return response.json<OrgRecord[]>();
    },
    [httpClient],
  );

  const retrieveRobotoUser = useCallback(
    async (signal?: AbortSignal): Promise<UserRecord> => {
      const response = await httpClient.get("v1/users", { signal });
      return response.json<UserRecord>();
    },
    [httpClient],
  );

  const signedInUserId = authState.isAuthenticated
    ? authState.currentUser?.user_id
    : undefined;
  const { data: myOrgRoles = null } = useQuery({
    queryKey: myOrgRolesQueryKeys.forUser(signedInUserId ?? ""),
    queryFn: ({ signal }) => retrieveMyOrgRoles(signal),
    enabled: signedInUserId !== undefined,
    staleTime: MY_ORG_ROLES_STALE_TIME_MS,
  });

  // Boot and sign-in fetch the roles alongside the user and cache them before
  // setting the user, so role-gated UI never renders without them.
  const cacheMyOrgRoles = useCallback(
    (userId: string, roles: OrgUserRecord[]) => {
      queryClient.setQueryData(myOrgRolesQueryKeys.forUser(userId), roles);
    },
    [queryClient],
  );

  const refreshMyOrgRoles = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: myOrgRolesQueryKeys.all() }),
    [queryClient],
  );

  // Check auth status when app boots
  useEffect(() => {
    const abortController = new AbortController();

    seedFeatureFlags();

    const checkAuth = async () => {
      setAuthState((prevState) => ({
        ...prevState,
        pendingAuthOperationCount: prevState.pendingAuthOperationCount + 1,
      }));

      const [currentUser, availableOrganizations, myOrgRoles] =
        await Promise.all([
          retrieveRobotoUser(abortController.signal),
          retrieveOrgList(abortController.signal),
          retrieveMyOrgRoles(abortController.signal),
        ]);

      cacheMyOrgRoles(currentUser.user_id, myOrgRoles);
      setAuthState((prevState) => ({
        ...prevState,
        availableOrganizations,
        currentOrganization: orgStorage.getCurrentOrg(),
        currentUser,
        isAuthenticated: currentUser !== null,
      }));
    };

    checkAuth()
      .catch((reason) => {
        if (
          isAbortError(reason) ||
          (reason instanceof RobotoDomainException && reason.isClientError())
        ) {
          // Swallow error if op was aborted
          // or if it's a client error (likely: unauthorized error), which is expected when not signed in yet.
          return;
        }

        ErrorMonitoringService.captureError(reason);
      })
      .finally(() => {
        setAuthState((prevState) => ({
          ...prevState,
          initialAuthCheckComplete: !abortController.signal.aborted,
          pendingAuthOperationCount: prevState.pendingAuthOperationCount - 1,
        }));
      });

    return function abortActiveRequests() {
      abortController.abort();
    };
  }, [
    retrieveRobotoUser,
    retrieveOrgList,
    retrieveMyOrgRoles,
    seedFeatureFlags,
    cacheMyOrgRoles,
  ]);

  const signUp = async (
    emailAddress: string,
    password: string,
  ): Promise<string | null> => {
    if (authState.currentUser) {
      return "You are already signed in. Please sign out before creating another account.";
    }

    try {
      await amplifySignUp({
        username: emailAddress,
        password: password,
        options: {
          userAttributes: {
            email: emailAddress,
          },
          autoSignIn: true,
        },
      });

      return null;
    } catch (error: unknown) {
      if (
        error instanceof AuthError &&
        error.name === "UsernameExistsException"
      ) {
        return resumeUnverifiedSignUp(emailAddress, password, error.message);
      }

      if (error instanceof AuthError) {
        return error.message;
      }

      ErrorMonitoringService.captureError(error);
      return "There was an error signing up. Please try again.";
    }
  };

  // Someone who closed the tab before entering their code can only get a new
  // one by proving the password. Returns `takenMessage` for any other account.
  const resumeUnverifiedSignUp = async (
    emailAddress: string,
    password: string,
    takenMessage: string,
  ): Promise<string | null> => {
    try {
      const { isSignedIn, nextStep } = await amplifySignIn({
        username: emailAddress,
        password,
      });
      if (isSignedIn) {
        await amplifySignOut();
      }
      if (nextStep.signInStep !== "CONFIRM_SIGN_UP") {
        return takenMessage;
      }
    } catch {
      return takenMessage;
    }

    return resendVerificationCode(emailAddress);
  };

  const submitVerificationCode = async (
    username: string,
    code: string,
  ): Promise<string | null> => {
    try {
      await confirmSignUp({ username, confirmationCode: code });

      return null;
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        return error.message;
      }

      ErrorMonitoringService.captureError(error);
      return "There was an error confirming your account. Please try again.";
    }
  };

  /** `actuallySignInCallback` resolves `false` when no session was started. */
  const signInCommon = async (
    actuallySignInCallback: () => Promise<boolean>,
  ) => {
    try {
      setAuthState((prevState) => ({
        ...prevState,
        pendingAuthOperationCount: prevState.pendingAuthOperationCount + 1,
      }));

      if (!(await actuallySignInCallback())) {
        return null;
      }

      // Discard any matrix cached for a previous session in this tab, then
      // refetch for the user who just signed in.
      clearFeatureFlags();
      seedFeatureFlags();

      const [currentUser, availableOrganizations, myOrgRoles] =
        await Promise.all([
          retrieveRobotoUser(),
          retrieveOrgList(),
          retrieveMyOrgRoles(),
        ]);

      cacheMyOrgRoles(currentUser.user_id, myOrgRoles);
      setAuthState((prevState) => ({
        ...prevState,
        availableOrganizations,
        currentOrganization: getCurrentOrganization(),
        currentUser,
        isAuthenticated: currentUser !== null,
      }));

      return null;
    } catch (err: unknown) {
      if (isAbortError(err)) {
        return null;
      }

      setAuthState((prevState) => ({
        ...prevState,
        isAuthenticated: false,
        currentUser: null,
        currentOrganization: getCurrentOrganization(),
        availableOrganizations: null,
      }));

      if (err instanceof AuthError) {
        return err.message;
      }

      ErrorMonitoringService.captureError(err);
      return "There was an error signing in. Please try again.";
    } finally {
      setAuthState((prevState) => ({
        ...prevState,
        pendingAuthOperationCount: prevState.pendingAuthOperationCount - 1,
      }));
    }
  };

  const isAuthd = async () => {
    try {
      // This method throws if not authenticated
      // https://docs.amplify.aws/gen1/react/build-a-backend/auth/manage-user-session/
      await amplifyGetCurrentUser();
      return true;
    } catch {
      return false;
    }
  };

  const signIn = async (
    emailAddress: string,
    password: string,
  ): Promise<SignInResult> => {
    let emailNotVerified = false;

    const actuallySignInCallback = async () => {
      const isAuthenticated = await isAuthd();
      if (!isAuthenticated) {
        const { nextStep } = await amplifySignIn({
          username: emailAddress,
          password,
        });
        emailNotVerified = nextStep.signInStep === "CONFIRM_SIGN_UP";
      }
      return !emailNotVerified;
    };

    const message = await signInCommon(actuallySignInCallback);
    if (message !== null) {
      return { status: "failed", message };
    }
    if (emailNotVerified) {
      return {
        status: "emailNotVerified",
        message: "Your email address hasn't been verified yet.",
      };
    }
    return { status: "signedIn" };
  };

  const ssoSignIn = async (inviteId: string | null): Promise<string | null> => {
    const actuallySignInCallback = async () => {
      if (inviteId) {
        await signInWithRedirect({
          provider: { custom: Env.ssoSignInConfig?.cognitoProviderId || "" },
          customState: JSON.stringify({ inviteId: inviteId }),
        });
      } else {
        await signInWithRedirect({
          provider: { custom: Env.ssoSignInConfig?.cognitoProviderId || "" },
        });
      }
      return true;
    };

    return signInCommon(actuallySignInCallback);
  };

  const signOut = async (): Promise<string | null> => {
    try {
      await amplifySignOut();

      deleteCurrentOrganizationFromLocalStorage();
      return null;
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        return error.message;
      }

      ErrorMonitoringService.captureError(error);
      return "There was an error signing out. Please try again.";
    } finally {
      clearFeatureFlags();

      setAuthState((prevState) => ({
        ...prevState,
        availableOrganizations: null,
        currentOrganization: null,
        currentUser: null,
        isAuthenticated: false,
      }));
      // Also cancels a reread still in flight.
      queryClient.removeQueries({ queryKey: myOrgRolesQueryKeys.all() });
    }
  };

  const getCurrentUser = async (): Promise<UserRecord> => {
    if (authState.currentUser) {
      return authState.currentUser;
    }

    return retrieveRobotoUser();
  };

  const forgotPasswordInitiate = async (
    emailAddress: string,
  ): Promise<string | null> => {
    try {
      await resetPassword({ username: emailAddress });

      return null;
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        if (error.message) {
          return error.message;
        }
      }

      return "There was an error initiating the password reset process. Please try again.";
    }
  };

  const forgotPasswordComplete = async (
    emailAddress: string,
    code: string,
    newPassword: string,
  ): Promise<string | null> => {
    try {
      await confirmResetPassword({
        username: emailAddress,
        newPassword,
        confirmationCode: code,
      });

      return null;
    } catch (error: unknown) {
      if (error instanceof AuthError) {
        return error.message;
      }

      return "There was an error completing the password reset process. Please try again.";
    }
  };

  const parseJwt = (token: string): UserIdToken | null => {
    const base64Url = token.split(".")[1];
    if (base64Url === undefined) {
      return null;
    }
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    return JSON.parse(jsonPayload) as UserIdToken;
  };

  const getUserIdToken = async (): Promise<UserIdToken | null> => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (idToken === undefined) {
        return null;
      }
      return parseJwt(idToken);
    } catch (err: unknown) {
      ErrorMonitoringService.captureError(err);
      return null;
    }
  };

  const refreshOrgList = async () => {
    const orgs = await retrieveOrgList();

    setAuthState((prevState) => ({
      ...prevState,
      availableOrganizations: orgs,
    }));
  };

  const resendVerificationCode = async (emailAddress: string) => {
    try {
      await resendSignUpCode({ username: emailAddress });
      return null;
    } catch (e) {
      ErrorMonitoringService.captureError(e);
      return "Error sending code. Please try again.";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        initialAuthCheckComplete: authState.initialAuthCheckComplete,
        isAuthenticated: authState.isAuthenticated,
        isLoading: authState.pendingAuthOperationCount > 0,
        currentUser: authState.currentUser,
        currentOrganization: authState.currentOrganization,
        availableOrganizations: authState.availableOrganizations,
        myOrgRoles,
        signUp,
        submitVerificationCode,
        signIn,
        signOut,
        ssoSignIn,
        forgotPasswordInitiate,
        forgotPasswordComplete,
        getCurrentUser,
        getCurrentOrganization,
        setCurrentOrganization,
        deleteCurrentOrganization,
        getUserIdToken,
        refreshOrgList,
        refreshMyOrgRoles,
        resendVerificationCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
